import { API_ENDPOINTS } from '../config/apiConfig';
import { apiFetch } from '../config/apiClient';

// Types for learning activities
export interface LearningSession {
  id: string;
  studentId: string;
  studentName: string;
  studentPhone: string;
  packageId: string;
  packageName: string;
  subject: string;
  grade: string;
  series: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'ABANDONED' | 'EXPIRED' | 'ACTIVE';
  score: number;
  totalScore: number;
  currentQuestionIndex: number;
  totalQuestions: number;
  answeredQuestions: number;
  timeLeft: number;
  startTime: string;
  endTime?: string;
  lastActivityTime: string;
  openingDate: string;
  closingDate: string;
}

export interface StudentActivity {
  id: string;
  sessionId: string;
  questionId: string;
  questionIndex: number;
  startTime: string;
  endTime?: string;
  totalTimeTaken: number;
  answerIndex?: number;
  answerText?: string;
  result?: boolean;
  isMarkedForReview: boolean;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ANSWERED' | 'REVIEWED' | 'SKIPPED';
}

export interface StudentPerformance {
  totalSessions: number;
  completedSessions: number;
  averageScore: number;
  totalQuestionsAnswered: number;
  correctAnswers: number;
  accuracy: number;
  totalTimeSpent: number;
  subjects: string[];
  recentActivity: LearningSession[];
}

export interface SessionResult {
  sessionId: string;
  score: number;
  totalScore: number;
  accuracy: number;
  timeSpent: number;
  questionsAnswered: number;
  totalQuestions: number;
  subject: string;
  grade: string;
  completedAt: string;
}

class LearningService {
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Fetch all sessions for a student
   */
  async getSessionsByStudentId(studentId: string): Promise<LearningSession[]> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.learningApi}/sessions/student/${studentId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status === 'success' && data.data && data.data.sessions) {
        return data.data.sessions.map((session: any) => ({
          id: session.id,
          studentId: session.studentId,
          studentName: session.studentName,
          studentPhone: session.studentPhone,
          packageId: session.packageId,
          packageName: session.packageName || `${session.grade} ${session.subject} ${session.series}`,
          subject: session.subject,
          grade: session.grade,
          series: session.series,
          status: session.status,
          score: session.score || 0,
          totalScore: session.totalScore || 0,
          currentQuestionIndex: session.currentQuestionIndex || 0,
          totalQuestions: session.totalNoOfQuestions || 0, // Fixed: use totalNoOfQuestions from API
          answeredQuestions: session.answers ? session.answers.length : 0, // Added: calculate from answers array
          timeLeft: session.timeLeft || 0,
          startTime: session.startTime,
          endTime: session.endTime,
          lastActivityTime: session.lastActivityTime,
          openingDate: session.openingDate,
          closingDate: session.closingDate,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw new Error('Failed to fetch student sessions');
    }
  }

  /**
   * Fetch a specific session by ID
   */
  async getSession(sessionId: string): Promise<LearningSession | null> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.learningApi}/sessions/${sessionId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch session: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data) {
        return {
          id: data.id,
          studentId: data.studentId,
          studentName: data.studentName,
          studentPhone: data.studentPhone,
          packageId: data.packageId,
          packageName: data.packageName || `${data.grade} ${data.subject} ${data.series}`,
          subject: data.subject,
          grade: data.grade,
          series: data.series,
          status: data.status,
          score: data.score || 0,
          totalScore: data.totalScore || 0,
          currentQuestionIndex: data.currentQuestionIndex || 0,
          totalQuestions: data.totalNoOfQuestions || 0, // Fixed: use totalNoOfQuestions from API
          answeredQuestions: data.answers ? data.answers.length : 0, // Added: calculate from answers array
          timeLeft: data.timeLeft || 0,
          startTime: data.startTime,
          endTime: data.endTime,
          lastActivityTime: data.lastActivityTime,
          openingDate: data.openingDate,
          closingDate: data.closingDate,
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching session:', error);
      throw new Error('Failed to fetch session details');
    }
  }

  /**
   * Fetch session result/analytics
   */
  async getSessionResult(sessionId: string): Promise<SessionResult | null> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.learningApi}/sessions/${sessionId}/result`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch session result: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status === 'success' && data.data) {
        return {
          sessionId: data.data.sessionId || sessionId,
          score: data.data.score || 0,
          totalScore: data.data.totalScore || 0,
          accuracy: data.data.accuracy || 0,
          timeSpent: data.data.timeSpent || 0,
          questionsAnswered: data.data.questionsAnswered || 0,
          totalQuestions: data.data.totalQuestions || 0,
          subject: data.data.subject || '',
          grade: data.data.grade || '',
          completedAt: data.data.completedAt || '',
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching session result:', error);
      throw new Error('Failed to fetch session result');
    }
  }

  /**
   * Fetch activities for a session
   */
  async getSessionActivities(sessionId: string): Promise<StudentActivity[]> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.learningApi}/activities/session/${sessionId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch activities: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (Array.isArray(data)) {
        return data.map((activity: any) => ({
          id: activity.id,
          sessionId: activity.sessionId,
          questionId: activity.questionId,
          questionIndex: activity.questionIndex,
          startTime: activity.startTime,
          endTime: activity.endTime,
          totalTimeTaken: activity.totalTimeTaken || 0,
          answerIndex: activity.answerIndex,
          answerText: activity.answerText,
          result: activity.result,
          isMarkedForReview: activity.isMarkedForReview || false,
          status: activity.status,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching activities:', error);
      throw new Error('Failed to fetch session activities');
    }
  }

  /**
   * Calculate student performance from sessions
   */
  async getStudentPerformance(studentId: string): Promise<StudentPerformance> {
    try {
      const sessions = await this.getSessionsByStudentId(studentId);
      
      const completedSessions = sessions.filter(s => s.status === 'COMPLETED');
      const totalSessions = sessions.length;
      
      const totalScore = completedSessions.reduce((sum, session) => sum + session.score, 0);
      const totalPossibleScore = completedSessions.reduce((sum, session) => sum + session.totalScore, 0);
      const averageScore = totalPossibleScore > 0 ? (totalScore / totalPossibleScore) * 100 : 0;
      
      // Get unique subjects
      const subjects = Array.from(new Set(sessions.map(s => s.subject)));
      
      // Calculate total time spent (assuming timeLeft is in minutes)
      const totalTimeSpent = sessions.reduce((sum, session) => {
        if (session.status === 'COMPLETED' && session.timeLeft !== undefined) {
          // This is a rough calculation - you might want to store actual time spent
          return sum + (session.totalQuestions * 2); // Assume 2 minutes per question
        }
        return sum;
      }, 0);

      // Calculate actual questions answered and accuracy from session data
      const totalQuestionsAnswered = sessions.reduce((sum, session) => sum + session.answeredQuestions, 0);
      const correctAnswers = completedSessions.reduce((sum, session) => sum + session.score, 0);
      const accuracy = totalQuestionsAnswered > 0 ? (correctAnswers / totalQuestionsAnswered) * 100 : 0;

      return {
        totalSessions,
        completedSessions: completedSessions.length,
        averageScore: Math.round(averageScore * 10) / 10, // Round to 1 decimal
        totalQuestionsAnswered,
        correctAnswers,
        accuracy: Math.round(accuracy * 10) / 10,
        totalTimeSpent,
        subjects,
        recentActivity: sessions.slice(0, 5), // Last 5 sessions
      };
    } catch (error) {
      console.error('Error calculating performance:', error);
      throw new Error('Failed to calculate student performance');
    }
  }

  /**
   * End a session
   */
  async endSession(sessionId: string): Promise<boolean> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.learningApi}/sessions/${sessionId}/end`, {
        method: 'PUT',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to end session: ${response.statusText}`);
      }

      const data = await response.json();
      return data.status === 'success';
    } catch (error) {
      console.error('Error ending session:', error);
      throw new Error('Failed to end session');
    }
  }

  /**
   * Reset a session
   */
  async resetSession(sessionId: string): Promise<boolean> {
    try {
      const response = await apiFetch(`${API_ENDPOINTS.learningApi}/sessions/${sessionId}/reset`, {
        method: 'PUT',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to reset session: ${response.statusText}`);
      }

      const data = await response.json();
      return data.status === 'success';
    } catch (error) {
      console.error('Error resetting session:', error);
      throw new Error('Failed to reset session');
    }
  }

  /**
   * Fetch all sessions (paginated) for the LMS dashboard
   */
  async getAllSessions(page = 0, size = 20, academicYear?: number | null): Promise<{ sessions: LearningSession[]; page: number; size: number; totalElements: number }> {
    try {
      const yearParam = academicYear != null ? `&academicYear=${academicYear}` : '';
      const response = await apiFetch(`${API_ENDPOINTS.learningApi}/sessions/all?page=${page}&size=${size}${yearParam}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch all sessions: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.status === 'success' && data.data && data.data.sessions) {
        return {
          sessions: data.data.sessions.map((session: any) => ({
            id: session.id,
            studentId: session.studentId,
            packageId: session.packageId,
            packageName: session.packageName || `${session.grade} ${session.subject} ${session.series}`,
            subject: session.subject,
            grade: session.grade,
            series: session.series,
            status: session.status,
            score: session.score || 0,
            totalScore: session.totalScore || 0,
            currentQuestionIndex: session.currentQuestionIndex || 0,
            totalQuestions: session.totalNoOfQuestions || 0, // Fixed: use totalNoOfQuestions from API
            answeredQuestions: session.answers ? session.answers.length : 0, // Added: calculate from answers array
            timeLeft: session.timeLeft || 0,
            startTime: session.startTime,
            endTime: session.endTime,
            lastActivityTime: session.lastActivityTime,
            openingDate: session.openingDate,
            closingDate: session.closingDate,
            studentName: session.studentName,
            studentPhone: session.studentPhone,
          })),
          page: data.data.page,
          size: data.data.size,
          totalElements: data.data.totalElements,
        };
      }
      return { sessions: [], page: 0, size: 0, totalElements: 0 };
    } catch (error) {
      console.error('Error fetching all sessions:', error);
      throw new Error('Failed to fetch all sessions');
    }
  }
}

const learningService = new LearningService();
export default learningService;